import { Injectable } from '@angular/core';
import { PromptService } from '../promtp-service/prompt.service';
import { OrganizationConsumerService } from 'src/app/api/proxy/organization/organization-services';
import { OrganizationCodeModel, OrganizationTokenModel } from 'src/app/api/proxy/organization/organization-models';
import { firstValueFrom, timeout } from 'rxjs';
import { SecureStorageService } from '../storage/secure-storage.service';
import { SecureStorageKey } from '../storage/models/secure-storage-key.enum';
import { MPIConsumerService } from 'src/app/api/proxy/mpi/mpi-services';
import { ConsentUpdateModel, PatientModel } from 'src/app/api/proxy/mpi/mpi-models';
import { EventService } from '../events/event.service';
import UATDetector from 'src/app/plugins/uat-detector/uat-detector-plugin';
import { PointsService } from '../points-service/points.service';
import { UserInfoService } from '../auth-service/user-info-service';
import { IAppEnvironment } from 'src/app/api/proxy/app-environment/app-environment-models';
import { StorageKey } from '../storage/models/storage-key.enum';
import { LoadingService } from '../loading-service/loading.service';
import { consumerMyOrganizationGet } from 'src/app/api/proxy/organization/fn/consumer/consumer-my-organization-get';

@Injectable({
  providedIn: 'root',
})
export class OrganizationService {
  private NETWORK_TIMEOUT = 25000;
  private DISABLED_TIME_SECOND = 60;
  private INPUT_INVALID_ATTEMPTS = 10;
  private attempts = 0;
  private disabledSeconds = 0;

  private envJson?: string;

  constructor(protected _PromptService: PromptService,
    private _OrganizationConsumerService: OrganizationConsumerService,
    private _MPIConsumerService: MPIConsumerService,
    private _pointsService: PointsService
  ) {

    EventService.environmentLoaded.subscribe((env: IAppEnvironment) => {
      let jsn = JSON.stringify(env);
      if (jsn != this.envJson) {
        this.envJson = jsn;
        this.nextOrgRefresh = null;
      }
    });
  }

  public GetByTokenDisabledSeconds(): number {
    return this.disabledSeconds;
  }

  public async GetByTokenAsync(organizationToken: string): Promise<OrganizationTokenModel | null> {
    if (this.disabledSeconds > 0)
      return null;

    try {
      const response = await firstValueFrom(
        this._OrganizationConsumerService.consumerGetByTokenTokenGet({ token: organizationToken })
          .pipe(timeout(this.NETWORK_TIMEOUT))
      );

      this.attempts = 0;
      this.disabledSeconds = 0;
      return response;
    } catch (error) {

      this.attempts++;
      if (this.attempts > this.INPUT_INVALID_ATTEMPTS) {
        this.disabledSeconds = this.DISABLED_TIME_SECOND;
        setTimeout(() => {
          this.checkDisabledSeconds();
        }, 1000);
      }

      return null;
    }
  }

  private checkDisabledSeconds() {
    this.disabledSeconds--;
    if (this.disabledSeconds <= 0) {
      this.attempts = 0;
      this.disabledSeconds = 0;
    } else {
      setTimeout(() => {
        this.checkDisabledSeconds();
      }, 1000);
    }
  }

  public async SetDefaultOrganization(defaultOrg: OrganizationCodeModel): Promise<void> {
    await SecureStorageService.set(SecureStorageKey.DefualtOrginization, JSON.stringify(defaultOrg));
  }

  public async GetDefaultOrganization(): Promise<OrganizationCodeModel | null> {
    const defaultOrgStr = await SecureStorageService.get(SecureStorageKey.DefualtOrginization);
    if (!defaultOrgStr) {
      return null;
    }
    return JSON.parse(defaultOrgStr) as OrganizationCodeModel;
  }

  public async SubscriptionRequired(): Promise<boolean | null> {
    let defaultOrg = await this.GetDefaultOrganization();
    if (!defaultOrg)
      return null;

    if (defaultOrg.subscriptionRequired == undefined) {
      return null;
    }
    return defaultOrg.subscriptionRequired;
  }

  public async GrantConsentForOrganization(organizationId: string): Promise<boolean> {
    try {
      const request: ConsentUpdateModel = {
        granted: true
      };
      const response = await firstValueFrom(
        this._MPIConsumerService.consumerOrganizationConsentIdPost({ id: organizationId, body: request })
          .pipe(timeout(this.NETWORK_TIMEOUT))
      );

      //update the cache
      const patient = await firstValueFrom(
        this._MPIConsumerService.consumerGet()
          .pipe(timeout(this.NETWORK_TIMEOUT))
      );

      if (patient) {
        (patient as any).fetchedAt = new Date().toISOString();
        await SecureStorageService.set(StorageKey.PatientModel, JSON.stringify(patient));
      }


      return response;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'GrantConsentForOrganization');
    }
    return false;
  }


  private nextOrgRefresh: Date | null = null;
  private inUAT: boolean | null = null;
  private inLoadOrg: boolean = false;

  public async loadOrganization(source: string, forced: boolean = false): Promise<void> {

    if (this.inLoadOrg)
      return;

    this.inLoadOrg = true;
    let silentLoad = false;
    try {

      if (forced) {
        this.nextOrgRefresh = null;
      }

      const now = new Date();
      const org = await this.GetDefaultOrganization();
      if (org) {
        EventService.organizationConfigLoaded.emit(org);
        silentLoad = true;
      }

      if (this.nextOrgRefresh != null && this.nextOrgRefresh > now) {
        if (org) {
          return;
        }
      }


      this.nextOrgRefresh = new Date(now.getTime() + 60 * 60000); // 60 minutes in milliseconds
      try {

        if(silentLoad){
          LoadingService.addSilentUrl(consumerMyOrganizationGet.PATH);
        }
        const org = await firstValueFrom(this._OrganizationConsumerService.consumerMyOrganizationGet()
          .pipe(timeout(this.NETWORK_TIMEOUT)));
        if(silentLoad){
          LoadingService.removeSilentUrl(consumerMyOrganizationGet.PATH);
        }

        if (this.inUAT == null) {
          const pluginResponse = await UATDetector.detect();
          this.inUAT = pluginResponse.allowUAT;
        }

        if (this.inUAT) {
          org.aiChatEnabled = true;
        }

        if (!org.vivaScorePointsModuleEnabled) {
          const myPoints = await this._pointsService.currentBalance(true);
          if (myPoints && myPoints.currentPoints) {
            org.vivaScorePointsModuleEnabled = true;
          }
        }

        await this.SetDefaultOrganization(org);
        EventService.organizationConfigLoaded.emit(org);
        return;

      } catch (error) {
         if(silentLoad){
          LoadingService.removeSilentUrl(consumerMyOrganizationGet.PATH);
        }
        const org = await this.GetDefaultOrganization();
        if (org) {
          EventService.organizationConfigLoaded.emit(org);
        }
        return;

      }
    } finally {
      this.inLoadOrg = false;
    }



  }

  public async GetByIdAsync(organizationId: string): Promise<OrganizationTokenModel | null> {
    try {
      const response = await firstValueFrom(
        this._OrganizationConsumerService.consumerGetByIdOrganizationIdGet({ organizationId: organizationId })
          .pipe(timeout(this.NETWORK_TIMEOUT))
      );

      return response;
    } catch (error) {
      await this._PromptService.showNetworkConnectionError(error, 'GetByTokenAsync');
      return null;
    }
  }

  public async PersonConscentGranted(forced: boolean = false): Promise<boolean> {
    const myPatient = await SecureStorageService.get(StorageKey.PatientModel);
    let model: PatientModel | null = null;
    let cacheTimestamp: Date | null = null;

    if (myPatient) {
      try {
        model = JSON.parse(myPatient);
      } catch {
        model = null
      }
    }

    if (model) {
      const ts = (model as any).fetchedAt;
      if (ts) {
        cacheTimestamp = new Date(ts);
      }
    }

    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1); // Go back 1 day
    yesterday.setHours(23, 59, 59); // Set to 23:59:59.999

    if (model && cacheTimestamp && !forced) {
      const now = new Date();
      const ageHours = (now.getTime() - cacheTimestamp.getTime()) / (1000 * 60 * 60);

      const cacheDay = cacheTimestamp.getDate();

      const nowDay = now.getDate();

      const isSameDay = cacheDay === nowDay;

      if (isSameDay && model.vivaScoreConsentGranted) {
        return true;
      }
    }

    try {

      const response = await firstValueFrom(
        this._MPIConsumerService.consumerGet()
          .pipe(timeout(this.NETWORK_TIMEOUT))
      );

      if (response) {
        (response as any).fetchedAt = new Date().toISOString();
        await SecureStorageService.set(StorageKey.PatientModel, JSON.stringify(response));
        return response.vivaScoreConsentGranted == true;
      }

    } catch (error) {
      return true; //lets not prompt on network issues
    }
    return false;
  }

  public async GrantPatientConscent(): Promise<boolean> {

    const myPatient = await SecureStorageService.get(StorageKey.PatientModel);
    let model: PatientModel | null = null;
    if (myPatient) {
      try {
        model = JSON.parse(myPatient);
      } catch {
        model = null
      }
    }

    let orgId: string | null = null;

    if (model == null || !model.organizationId) {
      var defaultOrg = await this.GetDefaultOrganization();
      if (defaultOrg)
        orgId = defaultOrg.id!;
    } else {
      orgId = model.organizationId;
    }

    if (orgId) {
      return await this.GrantConsentForOrganization(orgId);
    }

    return false;

  }

}
